import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import TooltipMarkIcon from '../icons/TooltipMark.svg';
import './DisclaimerTooltip.css';

type Props = {
  tooltipText?: string;
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
  tooltipClassName?: string;
  /** When true, tooltip opens above the icon to avoid covering content below */
  positionAbove?: boolean;
}

const DisclaimerTooltip: React.FC<Props> = ({ tooltipText = '', isOpen, onToggle, className, tooltipClassName, positionAbove = false }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  // When positionAbove: use portal + fixed positioning so tooltip is never clipped by parent overflow
  useEffect(() => {
    if (positionAbove && isOpen && iconRef.current) {
      const updatePortalPosition = () => {
        if (!iconRef.current || !tooltipRef.current) return;
        const iconRect = iconRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const padding = 16;
        const gap = 8;
        let left = iconRect.left + (iconRect.width / 2) - (tooltipRect.width / 2);
        const top = iconRect.top - tooltipRect.height - gap;
        if (left < padding) left = padding;
        if (left + tooltipRect.width > viewportWidth - padding) left = viewportWidth - padding - tooltipRect.width;
        setPortalStyle({
          position: 'fixed',
          top,
          left,
          zIndex: 2147483647,
          visibility: 'visible',
        });
      };
      // Initial render: hide until we have dimensions (tooltip ref is set after portal mounts)
      setPortalStyle({ position: 'fixed', zIndex: 2147483647, visibility: 'hidden', top: 0, left: 0 });
      const timeoutId = setTimeout(updatePortalPosition, 50);
      window.addEventListener('resize', updatePortalPosition);
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', updatePortalPosition);
        setPortalStyle({});
      };
    }
    if (!isOpen) setPortalStyle({});
  }, [positionAbove, isOpen, tooltipText]);

  // Calculate best position for tooltip (when not positionAbove)
  useEffect(() => {
    if (!positionAbove && isOpen && tooltipRef.current && iconRef.current) {
      const timeoutId = setTimeout(() => {
        if (tooltipRef.current && iconRef.current) {
          const tooltip = tooltipRef.current;
          const icon = iconRef.current;
          const tooltipRect = tooltip.getBoundingClientRect();
          const iconRect = icon.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const padding = 16;
          const isMobile = viewportWidth < 768;
          const tooltipWidth = tooltipRect.width;
          const iconCenter = iconRect.left + (iconRect.width / 2);
          let idealLeft = iconCenter - (tooltipWidth / 2);
          let idealRight = idealLeft + tooltipWidth;
          const wouldOverflow = (idealRight > viewportWidth - padding) || (idealLeft < padding);
          if (wouldOverflow && isMobile) {
            const screenCenter = viewportWidth / 2;
            const offsetFromIcon = screenCenter - iconCenter;
            setTooltipStyle({ left: '50%', transform: `translateX(calc(-50% + ${offsetFromIcon}px))` });
          } else if (wouldOverflow) {
            let finalTransform = 'translateX(-50%)';
            if (idealRight > viewportWidth - padding) {
              const overflow = idealRight - (viewportWidth - padding);
              finalTransform = `translateX(calc(-50% - ${overflow + 20}px))`;
            } else if (idealLeft < padding) {
              const overflow = padding - idealLeft;
              finalTransform = `translateX(calc(-50% + ${overflow + 20}px))`;
            }
            setTooltipStyle({ left: '50%', transform: finalTransform });
          } else {
            setTooltipStyle({ left: '50%', transform: 'translateX(-50%)' });
          }
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
    if (!isOpen) setTooltipStyle({});
  }, [positionAbove, isOpen, tooltipText]);

  // Mark parent containers when tooltip is open
  useEffect(() => {
    if (isOpen && iconRef.current) {
      let parent = iconRef.current.parentElement;
      const markedElements: HTMLElement[] = [];
      const protectedContainerIds = ['content-container', 'main-container', 'sticky-price-text-ct'];
      let depth = 0;
      const maxDepth = 3; // Only mark immediate parents
      
      while (parent && parent !== document.body && depth < maxDepth) {
        const isProtected = protectedContainerIds.includes(parent.id);
        
        if (isProtected) {
          parent.setAttribute('data-tooltip-protected', 'true');
          break; // Stop when we hit protected containers
        }
        
        const style = window.getComputedStyle(parent);
        if (style.position === 'relative' || style.position === 'absolute' || style.position === 'fixed') {
          parent.setAttribute('data-tooltip-active', 'true');
          markedElements.push(parent);
        }
        
        parent = parent.parentElement;
        depth++;
      }
      
      return () => {
        markedElements.forEach(el => el.removeAttribute('data-tooltip-active'));
      };
    }
  }, [isOpen]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        onToggle();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Close tooltip when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        onToggle();
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scroll events
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, onToggle]);

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={`bg-[#2E3E67] text-white p-3 rounded-lg shadow-lg min-w-[200px] sm:min-w-[300px] disclaimer-tooltip ${tooltipClassName || ''}`}
      style={
        positionAbove
          ? {
              backgroundColor: '#2E3E67',
              opacity: 1,
              whiteSpace: 'pre-line' as const,
              maxWidth: 'calc(100vw - 32px)',
            }
          : {
              backgroundColor: '#2E3E67',
              opacity: 1,
              left: tooltipStyle.left || '50%',
              transform: tooltipStyle.transform || 'translateX(-50%)',
              whiteSpace: 'pre-line' as const,
              zIndex: 2147483647,
              maxWidth: 'calc(100vw - 32px)',
            }
      }
    >
      <div className="text-sm">{tooltipText}</div>
      {positionAbove ? (
        <div
          className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0"
          style={{
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid #2E3E67',
          }}
        />
      ) : (
        <div
          className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0"
          style={{
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid #2E3E67',
          }}
        />
      )}
    </div>
  );

  return (
    <div ref={iconRef} className={`relative ${className || ''}`}>
      <img
        src={TooltipMarkIcon}
        alt="Info"
        className="w-3 h-3 cursor-pointer"
        onClick={onToggle}
      />
      {isOpen && positionAbove
        ? createPortal(
            <div style={{ position: 'fixed', zIndex: 2147483647, ...portalStyle }}>{tooltipContent}</div>,
            document.body
          )
        : null}
      {isOpen && !positionAbove && (
        <div
          ref={tooltipRef}
          className={`absolute top-6 bg-[#2E3E67] text-white p-3 rounded-lg shadow-lg min-w-[200px] sm:min-w-[300px] disclaimer-tooltip ${tooltipClassName || ''}`}
          style={{
            backgroundColor: '#2E3E67',
            opacity: 1,
            left: tooltipStyle.left || '50%',
            transform: tooltipStyle.transform || 'translateX(-50%)',
            whiteSpace: 'pre-line',
            zIndex: 2147483647,
            maxWidth: 'calc(100vw - 32px)',
            right: 'auto',
          }}
        >
          <div className="text-sm">{tooltipText}</div>
          <div
            className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid #2E3E67',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DisclaimerTooltip;


